import { useMemo } from 'react';
import { IMissedMatchOffer } from '@nkr/models/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ReportWrapper } from './styles';
import { Label } from 'react-bootstrap';

interface IProps {
    data: IMissedMatchOffer[]
}

const Grid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {

        e.api.sizeColumnsToFit();

        e.columnApi.applyColumnState({
            state: [
                {
                    colId: 'recipientCenter',
                    sort: 'asc'
                }
            ]
        });
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const cols: ColDef[] = [
        {
            colId: 'swapNumber',
            // field: 'recipientCenter',
            headerName: 'Swap #',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap #</SortableHeaderComponent>,
            width: 85,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMissedMatchOffer;

                if (data.chainId == 0 && data.clusterNumber == 0 && data.positionNumber == 0) {
                    return <>--</>;
                }

                return <>{data.chainId}/{data.clusterNumber}/{data.positionNumber}</>;
            }
        },
        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'Recipient Center',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Center</SortableHeaderComponent>,
            width: 145,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false,
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Alias</SortableHeaderComponent>,
            width: 145,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false
        },
        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'Donor Center',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Center</SortableHeaderComponent>,
            width: 155,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false
        },
        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor Alias',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Alias</SortableHeaderComponent>,
            width: 105,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false
        },
        {
            colId: 'offerDateTime',
            field: 'offerDateTime',
            headerName: 'Offer Date/Time',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Offer Date/Time</SortableHeaderComponent>,
            width: 125,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false
        },
        {
            colId: 'deadlineDateTime',
            field: 'deadlineDateTime',
            headerName: 'Deadline Date/Time',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Deadline Date/Time</SortableHeaderComponent>,
            width: 145,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false
        },
        {
            colId: 'decisionDateTime',
            field: 'decisionDateTime',
            headerName: 'Decision Date/Time',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Decision Date/Time</SortableHeaderComponent>,
            width: 145,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMissedMatchOffer;

                return data.decisionDateTime == '' ? <Label>no decision</Label> : <>{data.decisionDateTime}</>;
            }
        },
        {
            colId: 'decision',
            headerName: 'Decision',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Decision</SortableHeaderComponent>,
            width: 165,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMissedMatchOffer;

                if (data.wasAcceptedNoIssues) {
                    return <div>Accepted - No Issues</div>;
                }

                if (data.wasDeclinedUnacceptable) {
                    return <>Declined</>;
                }

                if (data.wasAcceptedNotConfident) {
                    return <>Accepted - Not Confident</>;
                }

                if (data.wasDeclinedUnacceptableAbove99Cpra) {
                    return <>Declined - Above 99 cPRA</>;
                }

                if (data.wasAcceptedNotConfidentAbove99Cpra) {
                    return <>Accepted - Not Confident - Above 99 cPRA</>;
                }

                if (data.wasDeclinedSurgeonUnavailable) {
                    return <>Declined - Surgeon Unavailable</>;
                }

                return <Label>No decision</Label>;
            }
        }
    ]

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={30}
                groupHeaderHeight={30}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No donor record review &amp; VXM records recorded late...</div>}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
            />
        </ReportWrapper>
    );
}

export default Grid;